/**
 * Carrying a purchase across sign-in (Design §14).
 *
 * Buy on a public product page is a full-page navigation to another origin, and `makeReturnURL()`
 * keeps only origin, path and query — so the customer comes back having lost which price, which
 * currency and which coupon they chose, which is exactly the body `POST /api/checkout/session`
 * needs. Putting those in the query string is the obvious fix and the wrong one: `returnTo` becomes
 * a **query parameter on the AbstractID authorize URL**, where the identity provider logs and
 * stores it. A discount code worth money would then live in someone else's request logs.
 *
 * So the choice is written into `sessionStorage` under a random nonce and only the nonce travels
 * through the URL. `sessionStorage` rather than `localStorage` because an abandoned intent should
 * die with the tab, and because a stale one in a second tab must not resume someone else's price.
 *
 * Nothing here throws. Private-mode Safari raises on `sessionStorage` access, and the one caller is
 * a Buy click handler: a throw there is a button that does nothing at all, where the degraded path
 * is a nonce that simply fails to resolve and lands on `/Checkout/Start`'s "could not be resumed"
 * state — which at least tells the customer what to do next.
 */

import type { AcceptedClickwrap, SelectedProductOption } from '../AppTypes';

export type PurchaseIntent = {
	priceUUID: string;
	couponCode: string | null;
	currency: string;
	productSlug: string;
	/**
	 * Seats, for a per-seat price (§9.7). Carried across sign-in with everything else: a customer
	 * who picked forty seats and was sent to sign in must not come back to one, and re-deriving it
	 * afterwards is impossible — the number was never anywhere but this form.
	 */
	seats: number | null;
	/**
	 * The terms ticked on the product page (§4.7), each with the version shown.
	 *
	 * Carried across sign-in for the same reason as the seat count: it was chosen in a form that no
	 * longer exists by the time the session is created, and re-deriving it is impossible.
	 *
	 * A signed-out visitor therefore ticks before authenticating. The server still refuses a
	 * purchase whose terms are missing or stale, so what this affects is *who* the tick is
	 * attributed to — the person who then signed in on the same screen. Asking again after sign-in
	 * would be stricter; it would also put a terms page between Buy and checkout, which is what the
	 * inline design exists to avoid.
	 *
	 * A term that asks a question carries its answer here too, for exactly the reason the seat
	 * count is here: it was typed into a form that no longer exists by the time the session is
	 * created, and it is the one thing on the row nothing else can re-derive.
	 */
	acceptedClickwraps: AcceptedClickwrap[];
	/**
	 * The options ticked on the product page (§4.8), each with the version shown.
	 *
	 * Carried across sign-in for the reason the terms and the seat count are: it was chosen in a
	 * form that no longer exists by the time the session is created, and it cannot be re-derived —
	 * an option is *offered*, so an intent that lost it looks exactly like a buyer who took none,
	 * and the customer would be charged full price for an obligation they agreed to keep.
	 */
	selectedOptions: SelectedProductOption[];
	/**
	 * Where the buyer is buying from (§10.1), when the store had to ask.
	 *
	 * Empty on every store that lets the provider compute tax, and on every purchase after the
	 * first — the question is only asked when the store holds no country for this customer. It
	 * travels here for the same reason the seat count does: it was answered in a window that no
	 * longer exists by the time the session is created, and nothing else can re-derive it.
	 */
	billingCountry: string;
	/**
	 * The company the purchase is being made by, when the buyer named one (§10.1).
	 *
	 * Travels beside the country for the same reason and on the same screen: it is what makes the
	 * sale a business sale, so it has to reach `createSession` with the country it qualifies, not
	 * on a billing page the customer visits after the first invoice has already been taxed.
	 */
	billingCompany: string;
	/**
	 * The professional-individual declaration (§10.1), or null where the box was not offered — a
	 * country that does not take professionals as businesses. Null sends nothing, so a declaration
	 * already on file is not withdrawn by a purchase that never asked.
	 */
	billingProfessional?: boolean | null;
};

/** What actually goes into storage: the intent plus the clock reading the TTL is measured from. */
type StoredIntent = PurchaseIntent & { createdAt: number };

const KEY_PREFIX = 'checkout-intent:';

/**
 * Half an hour. An intent that survives a longer detour — a password reset, an email verification,
 * a walk away from the desk — describes a price the customer may no longer be looking at, and
 * resuming it silently would charge them for a screen they cannot see.
 */
const TTL_MS = 30 * 60 * 1000;

function storage(): Storage | null {
	try {
		return window.sessionStorage ?? null;
	} catch {
		return null;
	}
}

function makeNonce(): string {
	try {
		return crypto.randomUUID();
	} catch {
		// Nothing secret is protected by this value — it names a local entry and never leaves the
		// browser except as an opaque query parameter — so a weaker nonce is preferable to a Buy
		// button that throws where `crypto` is unavailable.
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	}
}

/** Stores the choice and returns the nonce to put in `returnTo`. */
export function writeIntent(intent: PurchaseIntent): string {
	const nonce = makeNonce();
	const stored: StoredIntent = { ...intent, createdAt: Date.now() };

	try {
		storage()?.setItem(`${KEY_PREFIX}${nonce}`, JSON.stringify(stored));
	} catch {
		// Quota exceeded, or storage disabled entirely. The nonce is still returned so the flow
		// continues to `/Checkout/Start`, which will report that the purchase could not be resumed.
	}

	return nonce;
}

/**
 * Reads an intent **and deletes it**. Single use: a session that was already started must not be
 * startable again by pressing back, which would create a second provider session for one purchase.
 *
 * Returns null when the entry is missing, unreadable or older than the TTL — all of which the
 * caller renders identically, because to the customer they are the same event.
 */
export function readIntent(nonce: string): PurchaseIntent | null {
	const store = storage();
	if (store === null || nonce.length === 0) return null;

	let raw: string | null = null;
	try {
		raw = store.getItem(`${KEY_PREFIX}${nonce}`);
		store.removeItem(`${KEY_PREFIX}${nonce}`);
	} catch {
		return null;
	}
	if (raw === null) return null;

	try {
		const parsed = JSON.parse(raw) as Partial<StoredIntent> | null;
		if (parsed === null || typeof parsed !== 'object') return null;
		if (typeof parsed.priceUUID !== 'string' || parsed.priceUUID.length === 0) return null;
		if (typeof parsed.createdAt !== 'number' || Date.now() - parsed.createdAt > TTL_MS) return null;

		return {
			priceUUID: parsed.priceUUID,
			couponCode: typeof parsed.couponCode === 'string' && parsed.couponCode.length > 0 ? parsed.couponCode : null,
			currency: typeof parsed.currency === 'string' ? parsed.currency : '',
			productSlug: typeof parsed.productSlug === 'string' ? parsed.productSlug : '',
			// A stored intent written before seats existed has none, which is not "one seat" — it
			// means "let the server apply the price's floor".
			seats:
				typeof parsed.seats === 'number' && Number.isInteger(parsed.seats) && parsed.seats > 0
					? parsed.seats
					: null,
			// An intent written before terms existed carries none, which is not "accepted nothing"
			// so much as "had nothing to accept" — and the server refuses either way if terms are
			// due, so the safe reading is the empty list.
			acceptedClickwraps: Array.isArray(parsed.acceptedClickwraps)
				? parsed.acceptedClickwraps
					.filter(
						(entry): entry is AcceptedClickwrap =>
							typeof entry === 'object' &&
							entry !== null &&
							typeof (entry as { uuid?: unknown }).uuid === 'string' &&
							typeof (entry as { version?: unknown }).version === 'number'
					)
					// The answer is rebuilt rather than passed through, so a stored intent whose
					// `inputValue` is a number or an object cannot reach the server as one. An
					// entry that carries none is left without the key: the server distinguishes an
					// unanswered question from a term that asks nothing, and `undefined` on the
					// wire would be sent as `null` by `JSON.stringify` and refused by a strict
					// schema that accepts a string or nothing at all.
					.map(entry =>
						typeof entry.inputValue === 'string' && entry.inputValue.length > 0
							? { uuid: entry.uuid, version: entry.version, inputValue: entry.inputValue }
							: { uuid: entry.uuid, version: entry.version }
					)
				: [],
			// An intent written before options existed carries none, which is the same answer as a
			// buyer who ticked nothing: the ordinary purchase, at list price. The version is
			// rebuilt rather than passed through, so a stored entry whose `version` is a string
			// cannot reach the server as one and be refused as stale.
			selectedOptions: Array.isArray(parsed.selectedOptions)
				? parsed.selectedOptions
					.filter(
						(entry): entry is SelectedProductOption =>
							typeof entry === 'object' &&
							entry !== null &&
							typeof (entry as { uuid?: unknown }).uuid === 'string' &&
							typeof (entry as { version?: unknown }).version === 'number'
					)
					.map(entry => ({ uuid: entry.uuid, version: entry.version }))
				: [],
			// An intent written before this question existed carries none, which reads the same as
			// never having been asked — the server asks again if it needs one.
			billingCountry: typeof parsed.billingCountry === 'string' ? parsed.billingCountry : '',
			// Same reading as the country above: an intent written before the question existed
			// carries none, which is a buyer who was never asked rather than one who said no.
			billingCompany: typeof parsed.billingCompany === 'string' ? parsed.billingCompany : '',
			billingProfessional: typeof parsed.billingProfessional === 'boolean' ? parsed.billingProfessional : null
		};
	} catch {
		return null;
	}
}
